import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle scroll on navigation with state
  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        const element = document.getElementById(location.state.scrollTo);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const scrollToSection = (sectionId) => {
    // If we're not on the home page, navigate there first
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: sectionId } });
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsOpen(false);
  };

  const goHome = () => {
    navigate('/');
    setIsOpen(false);
  };

  return (
    <nav className="fixed w-full z-50 backdrop-blur-md shadow-sm bg-white/95" role="navigation" aria-label="Main navigation">
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-rebel-coral focus:text-white focus:px-4 focus:py-2 focus:rounded"
      >
        Skip to main content
      </a>
      <div className="container-max px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button
            onClick={goHome}
            className="flex items-center focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rebel-coral rounded"
            aria-label="Go to home page"
          >
            <img src="/logo.png" alt="Rebel" className="h-10 w-auto" />
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="flex items-center space-x-8">
              {[
                { name: 'Problem', id: 'problem' },
                { name: 'Solution', id: 'solution' },
                { name: 'About', id: 'about' }
              ].map((item) => (
                <button
                  key={item.name}
                  onClick={() => scrollToSection(item.id)}
                  className="text-rebel-dark hover:text-rebel-coral transition-colors duration-200 font-medium focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-rebel-coral rounded min-h-[44px] px-3 touch-manipulation"
                  aria-label={`Navigate to ${item.name} section`}
                >
                  {item.name}
                </button>
              ))}
              <button
                onClick={() => scrollToSection('contact')}
                className="bg-rebel-coral text-white px-6 py-2 rounded-full font-semibold hover:bg-rebel-orange transition-all duration-200 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-rebel-dark min-h-[44px] touch-manipulation"
                aria-label="Get started with Rebel"
              >
                Get Started
              </button>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-rebel-dark hover:text-rebel-coral focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rebel-coral rounded p-2 min-h-[44px] min-w-[44px] touch-manipulation"
              aria-label={isOpen ? "Close menu" : "Open menu"}
              aria-expanded={isOpen}
              aria-controls="mobile-menu"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div id="mobile-menu" className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 pt-2 pb-3 space-y-1">
            {[
              { name: 'Problem', id: 'problem' },
              { name: 'Solution', id: 'solution' },
              { name: 'About', id: 'about' },
              { name: 'Get Started', id: 'contact' }
            ].map((item) => (
              <button
                key={item.name}
                onClick={() => scrollToSection(item.id)}
                className="text-rebel-dark hover:text-rebel-coral block px-3 py-2 rounded-md text-base font-medium w-full text-left transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rebel-coral min-h-[44px] touch-manipulation"
                aria-label={`Navigate to ${item.name} section`}
              >
                {item.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;